'use client';

import { useState, useEffect } from 'react';
import { CommonTranslations } from '@/lib/translations/types';

interface CookieBannerProps {
  translations: CommonTranslations;
}

export default function CookieBanner({ translations }: CookieBannerProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already accepted cookies
    // Only run on client side
    if (typeof window !== 'undefined') {
      const cookieAccepted = localStorage.getItem('cookie-banner-accepted');
      if (!cookieAccepted) {
        setIsVisible(true);
        
        // Auto-hide after 30 seconds
        const autoHideTimer = setTimeout(() => {
          setIsVisible(false);
          handleAccept();
        }, 30000);

        // Cleanup timer on unmount
        return () => {
          clearTimeout(autoHideTimer);
        };
      }
    }
  }, []);


  const handleAccept = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('cookie-banner-accepted', 'true');
    }
    setIsVisible(false);
  };


  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-xs sm:max-w-sm bg-white border border-gray-200 rounded-lg shadow-lg animate-in slide-in-from-bottom duration-300">
      <div className="p-3 sm:p-4">
        <div className="flex items-start gap-2 sm:gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-1">
              {translations.cookieBanner.title}
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed">
              {translations.cookieBanner.privacyMessage}
            </p>
          </div>
          <button
            onClick={handleAccept}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1 rounded p-1"
            aria-label="Close"
          >
            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
